import { useScrollProgress } from '@bridebook/aura';
import { useMotionValueEvent, useScroll } from 'motion/react';
import { RefObject, useCallback, useRef, useState } from 'react';

type UseScrollSyncCarouselOptions = {
  photoCount: number;
  contentRef: RefObject<HTMLElement>;
  enabled?: boolean;
  targetContainerScroll?: boolean;
};

const progressToIndex = (progress: number, count: number) =>
  Math.min(Math.floor(progress * count), count - 1);

/**
 * Syncs carousel photo index to scroll position.
 *
 * Two scroll modes (determined by targetContainerScroll):
 * - Container scroll (true): For preview panels where content scrolls inside container.
 * - Document scroll (false): For guest page where entire document scrolls.
 *
 * Note: Both scroll trackers are initialized due to React hooks rules.
 * Each handler early-returns when its mode is not active.
 */
export const useScrollSyncCarousel = ({
  photoCount,
  contentRef,
  enabled = true,
  targetContainerScroll = false,
}: UseScrollSyncCarouselOptions) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const photoCountRef = useRef(photoCount);
  photoCountRef.current = photoCount;

  const updateIndex = useCallback((progress: number) => {
    const newIndex = progressToIndex(progress, photoCountRef.current);
    setActiveIndex((current) => (current !== newIndex ? newIndex : current));
  }, []);

  // Both hooks must be called unconditionally (React hooks rules)
  const { scrollYProgress } = useScrollProgress({
    container: contentRef,
  });
  const { scrollY } = useScroll();

  // Container scroll mode: tracks scrollYProgress of the content panel
  useMotionValueEvent(scrollYProgress, 'change', (progress) => {
    if (!enabled || !targetContainerScroll || photoCountRef.current <= 1) return;
    updateIndex(progress);
  });

  // Document scroll mode: calculates progress from content panel's viewport position
  useMotionValueEvent(scrollY, 'change', () => {
    if (!enabled || targetContainerScroll || photoCountRef.current <= 1) return;

    const content = contentRef.current;
    if (!content) return;

    const rect = content.getBoundingClientRect();
    const scrollableDistance = rect.height - window.innerHeight;
    if (scrollableDistance <= 0) return;

    const progress = Math.max(0, Math.min(1, -rect.top / scrollableDistance));
    updateIndex(progress);
  });

  // Reset index when photo count changes
  const prevCountRef = useRef(photoCount);
  if (prevCountRef.current !== photoCount) {
    prevCountRef.current = photoCount;
    const clamped = photoCount <= 0 ? 0 : Math.min(activeIndex, photoCount - 1);
    if (activeIndex !== clamped) {
      setActiveIndex(clamped);
    }
  }

  return { activeIndex };
};
